<template>
  <div>
    <variants-table
      :analytics-date-range="analyticsDateRange"
      :has-custom-variants-share="!!model.custom_variant_share"
      :channel="channel"
      v-if="model.ab"
    />

    <channel-metrics-chart
      class="channel-metric-ct m-top-20"
      v-if="chartsData[channel]"
      :chart-type="chartType"
      :title="$t('CampaignChannelAnalytics_VariantTesting_Statistics')"
      :key="key"
      :icon="`dashboard_${channel}`"
      :chart-data="chartsData[channel] || {}"
      :variant-testing-channel="channel"
      :selected-method="method"
      @on-method-change="onMethodChange"
      :height="
        getChannelHeight((chartsData[channel] || {}).datasets || []).length
      "
    />
  </div>
</template>

<script>
import VariantsTable from '@/modules/campaign-manage/components/analytics/VariantsTable.vue'
import ChannelMetricsChart from '@/common/components/ChannelMetricsChart.vue'
import _ from 'lodash'
import {
  CHART_COLORS,
  COLORS
} from '@/modules/campaign-manage/helpers/ChartColours'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import AnalyticsMixin from '@/modules/campaign-manage/mixins/AnalyticsMixin'
import { statMethods } from '@/modules/campaign-manage/enum/VariantTestingMetrics'

export default {
  name: 'VariantTesting',
  components: { ChannelMetricsChart, VariantsTable },
  mixins: [AnalyticsMixin],
  props: {
    channel: {
      type: String,
      required: true
    },

    analyticsDateRange: {
      type: Object,
      required: true
    },

    model: {
      type: Object,
      required: true
    }
  },
  mounted() {
    this.method = this.methods[this.channel][0]
    this.fetchData()
  },
  data() {
    return {
      key: null,
      method: null,
      statMethods,
      reportData: {},
      reportTotal: {}
    }
  },
  computed: {
    methods() {
      const methods = _.cloneDeep(this.statMethods)

      if (this.$store.state.app.features.showInboxOpenStats) {
        methods.inbox.push('inbox_opened', 'inbox_open_rate')
      }

      return methods
    },
    allVariants() {
      return [...this.model.ab_variants, ...(this.model.ab_cg ? ['CG'] : [])]
    },
    chartsData() {
      let data = this.convertData(_.cloneDeep(this.reportData))
      Object.keys(data).forEach(channel => {
        const height = this.getChannelHeight(data[channel].datasets.length)
        data[channel].datasets.forEach((item, index) => {
          const color = COLORS[index] || COLORS[0]
          data[channel].datasets[index].fill = true
          data[channel].datasets[index].lineTension = 0
          data[channel].datasets[index].borderColor = CHART_COLORS[color]
          data[channel].datasets[index].backgroundColor = ctx => {
            return this.generateLineGradient(ctx, color, height)
          }

          if (
            this.method.includes('_click_rate') ||
            this.method.includes('_open_rate')
          ) {
            data[channel].datasets[index].yAxisID = 'y1'
          }
        })
      })

      return data
    },
    chartData() {
      return this.chartsData[this.channel] || {}
    }
  },
  methods: {
    convertData(data) {
      let chartData = {}
      Object.keys(data).forEach(channel => {
        const channelData = data[channel]['data'] || []
        chartData[channel] = { labels: Object.keys(channelData), datasets: [] }

        this.allVariants.forEach(variant => {
          let datasetData = Object.keys(channelData).map(
            date => parseInt((channelData[date] || {})[variant]) || 0
          )
          chartData[channel].datasets.push({
            label: this.getLabel(variant),
            data: datasetData,
            total: this.getTotal(variant)
          })
        })
      })

      return chartData
    },
    getTotal(variant) {
      if (
        this.method.includes('_click_rate') ||
        this.method.includes('_open_rate')
      ) {
        return _.get(this.reportTotal, `${this.channel}.data.${variant}`, 0)
      }

      return null
    },
    getLabel(variant) {
      if (variant === 'CG') {
        return 'CG – ' + this.$t('CampaignChannelAnalytics_VariantTesting_CG')
      }
      const title =
        (this.model.variants.find(item => item.key === variant) || {}).title ||
        null
      return title ? variant + ' – ' + title : variant
    },
    onMethodChange(method) {
      if (this.method !== method) {
        this.method = method
        this.fetchData()
      }
    },
    fetchData() {
      if (!this.channel || !this.method) {
        return
      }

      RestApi(
        projectEndpoint({
          method: 'get',
          url: `campaigns/${this.$route.query.id}/analytics/engagement-chart-data`,
          params: {
            campaign_id: this.$route.query.id,
            startDate: this.analyticsDateRange.date_from,
            endDate: this.analyticsDateRange.date_to,
            channels: [this.channel],
            method: `campaign_variants_${this.method}`,
            ids: this.allVariants.join(','),
            total:
              this.method.includes('_click_rate') ||
              this.method.includes('_open_rate')
                ? `campaign_variants_${this.method}_total`
                : null
          }
        })
      )
        .then(({ data }) => {
          this.reportData = data.data
          this.reportTotal = data.total
          this.$nextTick(() => {
            this.key = new Date().getTime()
          })
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    }
  },

  watch: {
    analyticsDateRange: {
      handler: function() {
        this.fetchData()
      },
      deep: true
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/analytics/VariantTesting.vue